import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import ArticleLeadCapture from "@/components/site/ArticleLeadCapture";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import { INSIGHTS, getInsight, formatInsightDate, readTimeLabel, INSIGHT_AUTHOR } from "@/data/insights";
import { ARTICLE_TOOLS } from "@/data/articlesTools";
import { INSIGHT_SOLUTION, getSolution } from "@/data/solutions";
import { TOOLS, publishedBySlug } from "@/data/tools/registry";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";
import { useHoverParallax } from "@/lib/useHoverParallax";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

export default function InsightArticlePage() {
  const { slug } = useParams();
  const article = getInsight(slug);
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const headerParallax = useHoverParallax(7);

  usePageMeta({
    title: article?.metaTitle,
    description: article?.metaDescription,
    image: article?.image ? `https://blezzings.co${article.image}` : undefined,
    imageAlt: article?.imageAlt,
    canonical: `https://blezzings.co/insights/${slug}`,
    breadcrumbs: article
      ? [
          { name: "Home", url: "https://blezzings.co/" },
          { name: "Insights", url: "https://blezzings.co/insights" },
          { name: article.title, url: `https://blezzings.co/insights/${article.slug}` },
        ]
      : undefined,
    article: article
      ? {
          headline: article.title,
          description: article.metaDescription,
          url: `https://blezzings.co/insights/${article.slug}`,
          image: article.image ? `https://blezzings.co${article.image}` : undefined,
          datePublished: article.date,
          dateModified: article.modifiedIso || article.date,
        }
      : undefined,
  });

  if (!article) return <Navigate to="/insights" replace />;

  const openBook = (source) => {
    track("book_consultation_open", { source });
    setBookOpen(true);
  };

  const idx = INSIGHTS.findIndex((i) => i.slug === article.slug);
  const next = INSIGHTS[(idx + 1) % INSIGHTS.length];

  return (
    <div data-testid="insight-article-page" className="relative">
      <Nav onBook={() => openBook("nav")} />

      {/* Article header */}
      <section className="pt-32 md:pt-40 pb-10 md:pb-14 bg-[#fafafa]">
        <div className={`${GRID} max-w-4xl`}>
          <SectionBadge label="The BLEZZINGS Perspective" />
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
            {article.title}
          </h1>
          <p className="mt-6 text-xl text-zinc-600 leading-relaxed max-w-2xl">
            {article.description}
          </p>
          <div className="mt-6 text-sm text-zinc-500">
            {formatInsightDate(article.date)} · {readTimeLabel(article)}
          </div>
          <p data-testid="article-byline" className="mt-4 text-[15px] text-zinc-700">
            By{" "}
            <Link
              to="/leadership"
              data-testid="article-byline-link"
              className="font-medium text-black underline underline-offset-4 hover:text-zinc-600 transition-colors"
            >
              {INSIGHT_AUTHOR.name}
            </Link>
            , {INSIGHT_AUTHOR.role}
          </p>
        </div>
      </section>

      {/* Header visual — full-bleed photograph with hover parallax */}
      {article.image && (
        <section
          data-testid="insight-header-visual"
          className="relative h-64 sm:h-80 md:h-[440px] overflow-hidden bg-zinc-200"
          onMouseMove={headerParallax.onMove}
          onMouseLeave={headerParallax.onLeave}
        >
          <div ref={headerParallax.ref} className="absolute -inset-4 parallax-frame">
            <img
              src={article.image}
              alt={article.imageAlt || article.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div
            className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"
            aria-hidden
          />
          {article.imageCaption && (
            <div className="absolute bottom-4 left-4 md:left-8 bg-black/80 backdrop-blur-md px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase text-white">
              {article.imageCaption}
            </div>
          )}
        </section>
      )}

      {/* Article body */}
      <article data-testid="insight-article-body" className="pb-16 md:pb-20 bg-[#fafafa]">
        <div className={`${GRID} max-w-4xl`}>
          {/* In this piece — section index for scannability */}
          {(() => {
            const tocItems = article.content
              .map((b, i) => ({ heading: b.heading, anchor: `section-${i}` }))
              .filter((x) => x.heading);
            return tocItems.length > 0 ? (
              <nav
                data-testid="insight-toc"
                aria-label="In this piece"
                className="border-t border-black/10 pt-8 mb-10 md:mb-12"
              >
                <div className="overline">In this piece</div>
                <ol className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                  {tocItems.map((t, n) => (
                    <li key={t.anchor}>
                      <a
                        href={`#${t.anchor}`}
                        data-testid={`insight-toc-link-${n}`}
                        className="group flex items-baseline gap-3 text-[15px] text-zinc-600 hover:text-black transition-colors"
                      >
                        <span className="text-xs text-zinc-400 tabular">
                          {String(n + 1).padStart(2, "0")}
                        </span>
                        <span className="group-hover:underline underline-offset-4">
                          {t.heading}
                        </span>
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            ) : null;
          })()}

          <div className={`${article.content.some((b) => b.heading) ? "" : "border-t border-black/10 pt-10 md:pt-12"} space-y-10`}>
            {article.content.map((block, i) => (
              <div key={i}>
                <div id={`section-${i}`} className="scroll-mt-28">
                  {block.heading && (
                    <h2 className="font-serif-display text-2xl sm:text-3xl leading-tight text-black mb-4">
                      {block.heading}
                    </h2>
                  )}
                  <div className="space-y-5 text-zinc-700 leading-[1.8] text-[16px]">
                    {block.paras.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                </div>
                {article.pullQuote && article.pullQuote.after === i && (
                  <figure
                    data-testid="insight-pull-quote"
                    className="my-12 md:my-14 md:-mx-10 lg:-mx-16 bg-black px-8 py-10 md:px-14 md:py-14"
                  >
                    <blockquote className="font-serif-display text-2xl sm:text-3xl lg:text-4xl leading-snug text-white">
                      &ldquo;{article.pullQuote.text}&rdquo;
                    </blockquote>
                    <figcaption className="mt-6 text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-medium">
                      The BLEZZINGS Perspective
                    </figcaption>
                  </figure>
                )}
              </div>
            ))}
          </div>

          {/* Reader lead capture — end of article, before promotional modules */}
          <ArticleLeadCapture articleSlug={article.slug} articleTitle={article.title} />

          {/* Put This Into Practice — the one tool that operationalises this article (published tools only) */}
          {(() => {
            const toolId = ARTICLE_TOOLS[article.slug];
            const toolRef = toolId ? TOOLS.find((t) => t.id === toolId) : null;
            const tool = toolRef && toolRef.status === "published" ? toolRef : null;
            if (!tool) return null;
            return (
              <div data-testid="article-tool" className="mt-10 border-2 border-black bg-white p-6 md:p-8">
                <div className="overline">Put This Into Practice</div>
                <div className="mt-3 font-serif-display text-xl md:text-2xl text-black">{tool.name}</div>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed max-w-2xl">{tool.hook}</p>
                <Link
                  to={tool.url}
                  data-testid="article-tool-link"
                  onClick={() => track("article_tool_click", { article: article.slug, tool: tool.id })}
                  className="group mt-4 inline-flex items-center gap-2 text-sm font-medium text-black hover:text-zinc-600 transition-colors"
                >
                  Use the free tool
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            );
          })()}

          {/* Author bio */}
          <div
            data-testid="article-author-bio"
            className="mt-14 border border-zinc-200 bg-white p-6 md:p-8"
          >
            <div className="overline">About the Author</div>
            <div className="font-serif-display text-xl md:text-2xl mt-2 text-black">
              {INSIGHT_AUTHOR.name}, {INSIGHT_AUTHOR.role}
            </div>
            <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed max-w-2xl">
              {INSIGHT_AUTHOR.bio}
            </p>
            <Link
              to="/leadership"
              data-testid="article-author-link"
              className="group mt-4 inline-flex items-center gap-2 text-sm font-medium text-black hover:text-zinc-600 transition-colors"
            >
              Meet the leadership
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          {/* Related capability — commercial next step from thought leadership */}
          {article.relatedService && (
            <div
              data-testid="article-related-service"
              className="mt-14 border border-zinc-200 bg-white p-6 md:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5"
            >
              <div>
                <div className="overline">Related Capability</div>
                <div className="font-serif-display text-xl md:text-2xl mt-2 text-black">
                  {article.relatedService.label}
                </div>
              </div>
              <Link
                to={article.relatedService.to}
                data-testid="article-related-service-link"
                onClick={() => track("article_service_click", { article: article.slug, service: article.relatedService.label })}
                className="group inline-flex items-center justify-center gap-2 text-sm font-medium text-black border border-black px-5 h-11 hover:bg-black hover:text-white transition-colors duration-300 shrink-0"
              >
                Explore the service
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          )}

          {/* Contextual solution link — only on mapped articles (spec §10) */}
          {(() => {
            const sid = INSIGHT_SOLUTION[article.slug];
            if (!sid) return null;
            const s = getSolution(sid);
            return (
              <p className="mt-6 text-sm text-zinc-600" data-testid="article-solution-link">
                Want this as a structured engagement?{" "}
                <Link
                  to={`/solutions/${s.id}`}
                  className="text-black font-medium underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors"
                >
                  See {s.name} — {s.descriptor}
                </Link>
              </p>
            );
          })()}

          {/* Article CTA */}
          <div className="mt-14 border border-black/10 bg-white p-8 md:p-10">
            <p className="font-serif-display text-2xl leading-snug text-black">
              Facing this question inside your business?
            </p>
            <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed max-w-xl">
              Tell us the growth or commercial challenge you are working
              through — we will help determine where the constraint sits.
            </p>
            <button
              data-testid="insight-cta-btn"
              onClick={() => openBook("insight-article")}
              className="group mt-6 inline-flex items-center gap-3 h-12 px-7 bg-black text-white text-[15px] font-medium hover:bg-zinc-800 transition-colors duration-300"
            >
              Discuss Your Growth Challenge
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
          </div>

          {/* Next article */}
          <div className="mt-12 pt-8 border-t border-black/10">
            <div className="overline">Read Next</div>
            <Link
              to={`/insights/${next.slug}`}
              data-testid="insight-next-link"
              className="group mt-4 flex items-baseline justify-between gap-6"
            >
              <span className="font-serif-display text-2xl sm:text-3xl leading-tight text-black group-hover:text-zinc-700 transition-colors">
                {next.title}
              </span>
              <ArrowUpRight className="h-6 w-6 shrink-0 text-zinc-400 transition-all duration-300 group-hover:text-black group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/insights"
              data-testid="insight-back-link"
              className="mt-6 inline-block text-sm font-medium text-zinc-500 hover:text-black transition-colors"
            >
              ← All Insights
            </Link>
          </div>
        </div>
      </article>

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => openBook("floating")} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
